/**
 * PricingLabel — renders a single pricing entry per SL-R-009 display rules.
 *
 * Rules (from SL-R-009 + decision-2026-05-30-subscription-as-registration):
 *   - trial:     price_cents === 0 && trial_days > 0 → "Trial · N days"
 *   - paid:      price_cents > 0 → "CCY amount / period"
 *   - not priced: price_cents === 0 && no trial → NOTHING is rendered (see below)
 *   - metric enrichment (user_block, token, etc.) rendered when metric field present
 *     (populated by WP-SL-M5; today falls back to simple amount display)
 *
 * ── The not-priced case (decision-2026-07-25-remove-fabricated-trial-cost) ────
 * A zero with no trial days used to fall through to formatAmount(0, currency) and
 * render a currency-formatted zero. That figure is composed at RUNTIME by
 * Intl.NumberFormat out of database values, so no static grep of the bundle could
 * ever have caught it — and it reached the public catalogue, where a zero reads as
 * a commitment exactly like a hardcoded one would.
 *
 * A zero price with no trial does not mean the service costs nothing; it means no
 * price has been recorded for it. So we render nothing rather than assert a figure
 * we do not have — the same rule CostDisclosure follows. When a real rate model
 * arrives with epic-subscription-billing, these rows get real values and this
 * branch stops being reachable.
 *
 * Design: inline, no external deps, uses existing CSS vars.
 *
 * WP-SL-V3 / DOPLA-686
 */

import type { PricingEntry } from '../hooks/usePlans';

interface PricingLabelProps {
  currency: string;
  pricing: PricingEntry;
  /** 'tile' = compact single line; 'detail' = two-line with sub-label */
  variant?: 'tile' | 'detail';
}

const PERIOD_LABELS: Record<string, string> = {
  monthly: 'mo',
  yearly: 'yr',
  weekly: 'wk',
  daily: 'day',
  quarterly: 'qtr',
};

const METRIC_LABELS: Record<string, (unit_size?: number) => string> = {
  user:          ()  => 'per user',
  user_block:    (n) => `per ${n?.toLocaleString() ?? '1000'} users`,
  token:         ()  => 'per token',
  api_call:      ()  => 'per call',
  storage_gb_day:()  => 'per GB·day',
  document:      ()  => 'per document',
  flat_period:   ()  => '',
  none:          ()  => '',
};

function formatAmount(cents: number, currency: string): string {
  const amount = cents / 100;
  return new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export default function PricingLabel({ currency, pricing, variant = 'tile' }: PricingLabelProps) {
  const { price_cents, billing_period, trial_days, metric, unit_size } = pricing;
  const isTrial = price_cents === 0 && trial_days > 0;
  const periodShort = PERIOD_LABELS[billing_period] ?? billing_period;

  if (isTrial) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 8px',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--dp-teal-050)',
          color: 'var(--dp-teal-700)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-eyebrow)',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        Trial · {trial_days} days
      </span>
    );
  }

  // Not priced: a zero that is not a trial is missing data, not a price of zero.
  if (!(price_cents > 0)) return null;

  const amountStr = formatAmount(price_cents, currency);
  const metricFn = metric ? METRIC_LABELS[metric] : undefined;
  const metricStr = metricFn ? metricFn(unit_size) : '';

  if (variant === 'detail') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body)',
          fontWeight: 600,
          color: 'var(--fg-1)',
        }}>
          {amountStr}
          <span style={{ fontWeight: 400, color: 'var(--fg-3)', fontSize: 'var(--fs-body-sm)' }}>
            {' '}/ {periodShort}
          </span>
        </span>
        {metricStr && (
          <span style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-meta)',
            color: 'var(--fg-3)',
          }}>
            {metricStr}
          </span>
        )}
      </div>
    );
  }

  // tile variant — compact
  return (
    <span style={{
      fontFamily: 'var(--font-sans)',
      fontSize: 'var(--fs-body-sm)',
      fontWeight: 500,
      color: 'var(--fg-2)',
    }}>
      {amountStr}
      <span style={{ fontWeight: 400, color: 'var(--fg-3)' }}>/{periodShort}</span>
      {metricStr && (
        <span style={{ fontWeight: 400, color: 'var(--fg-3)' }}> · {metricStr}</span>
      )}
    </span>
  );
}
